/**
 * Boundaries desk (Fleet Zones / Page173 residual).
 * Operator-facing copy is path-free. Seat posture drives survey vs write;
 * BE enforces exact tenant admin (or root) for geometry mutate.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "./vendor-maplibre.js";
import { html, F, Ico, Sec, KV, Card, Alert, Ticker, CraftSelect, go } from "./ui.js";
import * as D from "./data.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { bumpLiveZones } from "./live.js";
import { personnelRosterOf } from "./response-live.js";
import { boardLabelSay } from "./operator-say.js";
import { deviceHomedOnSite } from "./ops-map-region-filter.js";
import { placesPreferredCrewOptionsOf, placesEntityToneOf } from "./places-gaps.js";
import { parseLatLngPair, geocodeOpenMeteo } from "../lib/ops-map-geocode.js";

/** Pure: Zone dispatch AI priority choices — labels + one-line why. */
export const ZONE_DISPATCH_PRIORITY_OPTIONS = [
  {
    value: "nearest",
    label: "Nearest available",
    hint: "Rank by distance only — ignore Zone membership.",
  },
  {
    value: "inside_first",
    label: "Inside Zone first",
    hint: "People already inside this ring rank above outsiders.",
  },
  {
    value: "manager_first",
    label: "Manager, then inside Zone",
    hint: "Zone manager ranks first, then people inside the ring.",
  },
];

/** Pure: manager select rows from Live personnel + optional saved orphan. */
export function zoneDispatchManagerOptionsOf(roster, savedUsername) {
  const rows = Array.isArray(roster) ? roster : [];
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    const user = String((row && (row.user || row.username)) || "").trim();
    if (!user || seen.has(user.toLowerCase())) continue;
    seen.add(user.toLowerCase());
    const on = row && row.on === true;
    out.push({
      value: user,
      label: on ? `${user} · on call` : user,
    });
  }
  out.sort((a, b) => a.value.localeCompare(b.value));
  const saved = String(savedUsername || "").trim();
  if (saved && !seen.has(saved.toLowerCase())) {
    out.unshift({ value: saved, label: `${saved} · saved (not on roster)` });
  }
  return out;
}

/** Path-free operator copy for Zones desk failures (no API path leak). */
export function softFleetZonesErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|admin_required|insufficient|denied|not_authorized/.test(low)) {
    return "Your account cannot change zone geometry. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found|space_not_found/.test(low)) {
    return "That zone is not available.";
  }
  if (/\b400\b|invalid|required/.test(low)) {
    return "That change was not accepted.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|offline/.test(low)) {
    return "Boundary service is not ready right now.";
  }
  if (low === "zone_geometry_write_closed") {
    return "You can view zones, but geometry writes need a manager seat.";
  }
  if (/write_closed|seat_gated|admin_required/.test(low)) {
    return "Your account cannot change zone geometry. Ask a manager if you need access.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/fleet\/|\/zones|resource-fabric|http|GET |POST |PUT |DELETE |status_path|assert_min_role/.test(low)) {
    return "That action could not be completed.";
  }
  if (/^[a-z][a-z0-9_]{3,}$/.test(n) && n.includes("_")) {
    return "Your account cannot change zone geometry. Ask a manager if you need access.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

const KINDS = {
  camera:  { t: "Camera", glyph: "camera", tone: "#3DE1C8" },
  contact: { t: "Door contact", glyph: "door", tone: "#7FD1FF" },
  siren:   { t: "Siren", glyph: "siren", tone: "#FF9B5C" },
  gateway: { t: "Mast / gateway", glyph: "mast", tone: "#A78BFA" },
  sensor:  { t: "Sensor", glyph: "sensor", tone: "#9BE37D" },
  lidar:   { t: "Lidar", glyph: "lidar", tone: "#FFD166" },
  node:    { t: "Node", glyph: "chip", tone: "#8CA2A6" },
};
const kindOf = (k) => KINDS[k] || KINDS.node;

const centroid = (ring) => {
  const p = ring.slice(0, -1);
  return [p.reduce((a, x) => a + x[0], 0) / p.length, p.reduce((a, x) => a + x[1], 0) / p.length];
};
const areaM2 = (ring) => {
  const p = ring.slice(0, -1);
  let s = 0;
  for (let i = 0; i < p.length; i++) {
    const q = p[(i + 1) % p.length];
    s += p[i][0] * q[1] - q[0] * p[i][1];
  }
  const lat = centroid(ring)[1] * Math.PI / 180;
  return Math.abs(s / 2) * 111320 * 111320 * Math.cos(lat);
};

/** Pure: new draft space_id for Live upsert create. */
export function newZoneSpaceId() {
  return "cmc-z-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 8);
}

/**
 * Pure: auto-yard ring from mapped plant pins (WGS84).
 * One-click plant-yard draft — only when geometry write is open.
 * Pad is degrees (~110m per 0.001 at equator; slight oversize for membership).
 */
export function ringAroundPlantOf(register, padDeg = 0.0012) {
  const pts = (Array.isArray(register) ? register : [])
    .map((d) => (d && Array.isArray(d.ll) && d.ll.length >= 2 ? [Number(d.ll[0]), Number(d.ll[1])] : null))
    .filter((p) => p && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  if (!pts.length) return null;
  let minLng = pts[0][0];
  let maxLng = pts[0][0];
  let minLat = pts[0][1];
  let maxLat = pts[0][1];
  for (const [lng, lat] of pts) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  const pad = Math.max(0.00035, Number(padDeg) || 0.0012);
  // Slight square expand so a single pin still encloses the device.
  if (maxLng - minLng < pad * 0.4) {
    const m = (minLng + maxLng) / 2;
    minLng = m - pad * 0.5;
    maxLng = m + pad * 0.5;
  }
  if (maxLat - minLat < pad * 0.4) {
    const m = (minLat + maxLat) / 2;
    minLat = m - pad * 0.5;
    maxLat = m + pad * 0.5;
  }
  minLng -= pad;
  maxLng += pad;
  minLat -= pad;
  maxLat += pad;
  return [
    [minLng, minLat],
    [maxLng, minLat],
    [maxLng, maxLat],
    [minLng, maxLat],
    [minLng, minLat],
  ];
}

function guessKind(row) {
  const blob = [
    row && row.display_label, row && row.map_label, row && row.hw_code_resolved,
    row && row.product_hw_code, row && row.board_profile, row && row.chip_target,
  ].filter(Boolean).join(" ").toLowerCase();
  if (/\b(cam|camera|snap|vision|lidar)\b/.test(blob)) return /lidar/.test(blob) ? "lidar" : "camera";
  if (/\b(siren|strobe|horn|alarm)\b/.test(blob)) return "siren";
  if (/\b(door|contact|reed|magnet)\b/.test(blob)) return "contact";
  if (/\b(mast|gateway|bridge|hub)\b/.test(blob)) return "gateway";
  if (/\b(sensor|leak|smoke|pir|temp)\b/.test(blob)) return "sensor";
  return "node";
}

/** Pure: device pins for map from devices bus. */
export function fleetZoneDevicesOf(devicesPack) {
  const rows = (devicesPack && (devicesPack.items || devicesPack.devices)) || [];
  if (!Array.isArray(rows)) return [];
  const mapped = rows.map((r) => {
    if (!r) return null;
    const id = String(r.device_id || "").trim();
    if (!id) return null;
    const lat = r.map_lat == null ? null : Number(r.map_lat);
    const lng = r.map_lng == null ? null : Number(r.map_lng);
    const hasPos = Number.isFinite(lat) && Number.isFinite(lng);
    return {
      id,
      label: boardLabelSay(r, id),
      kind: guessKind(r),
      ll: hasPos ? [lng, lat] : null,
      zone: String(r.zone || "").trim(),
      group: String(r.notification_group || "").trim(),
      site: String(r.site_id || r.site || "").trim(),
    };
  }).filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const d of mapped) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    out.push(d);
  }
  return out;
}

/** Pure: surveyed zone rows — never invent rings when list is empty. */
export function fleetZoneListOf(zonesPack) {
  const rows = (zonesPack && (zonesPack.items || zonesPack.zones)) || [];
  if (!Array.isArray(rows)) return [];
  const list = [];
  for (const z of rows) {
    if (!z) continue;
    const label = String(z.label || z.zone_id || "").trim();
    if (!label) continue;
    list.push({
      id: z.zone_id || z.space_id || z.id || "",
      label,
      kind: String(z.kind || ""),
      ring: z.ring || null,
      deviceIds: z.device_ids || [],
      count: Number(z.device_count || 0),
      mismatch: z.label_mismatch_device_ids || [],
    });
  }
  return list;
}

/** Pure: zone KPI strip. */
export function fleetZoneKpisOf(surveyed, register) {
  const zones = Array.isArray(surveyed) ? surveyed.length : 0;
  const devices = Array.isArray(register) ? register.length : 0;
  const pinned = Array.isArray(register) ? register.filter((d) => d && d.ll).length : 0;
  const housed = Array.isArray(surveyed)
    ? surveyed.reduce((a, z) => a + (Number(z.count) || 0), 0)
    : 0;
  return { zones, devices, pinned, housed };
}

export function FleetZonesDesk({
  toast,
  onOpenGroups,
  siteId = "",
  embedded = false,
  floatOpen = false,
  onFloatOpen,
}) {
  const site = String(siteId || "").trim();
  const embed = embedded === true;
  const sheetOpen = floatOpen === true;
  const posture = useApi(path("get__fleet_zones_status"));
  const post = posture.data || {};
  /* Fail closed while posture loads / missing booleans. */
  const surveyOk = post.survey_allowed === true;
  const writeOk = post.geometry_write_allowed === true;
  const detail = post.detail || null;

  const zonesApi = useApi(
    surveyOk && site ? path("get__zones", { query: { site_id: site } }) : "",
  );
  const devicesApi = useApi(surveyOk ? path("get__devices") : "");
  const peopleApi = useApi(surveyOk ? "/personnel?limit=200" : "");
  const allRegister = useMemo(() => fleetZoneDevicesOf(devicesApi.data), [devicesApi.data]);
  const register = useMemo(
    () => allRegister.filter((device) => deviceHomedOnSite(device, site)),
    [allRegister, site],
  );
  const personnel = useMemo(() => personnelRosterOf(peopleApi.data), [peopleApi.data]);
  const surveyed = useMemo(() => fleetZoneListOf(zonesApi.data), [zonesApi.data]);
  const [rings, setRings] = useState({});
  const [pick, setPick] = useState("");
  const [draw, setDraw] = useState(false);
  const [pending, setPending] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [readyTick, setReadyTick] = useState(0);
  const [findQ, setFindQ] = useState("");
  const [findBusy, setFindBusy] = useState(false);
  const save_ = useAction();
  const del_ = useAction();
  const policySave = useAction();
  const envelope = save_.envelope;
  const ids = useRef({});
  const kpis = useMemo(() => fleetZoneKpisOf(surveyed, register), [surveyed, register]);
  const err = zonesApi.error || devicesApi.error;

  useEffect(() => {
    if (dirty) return;
    if (!surveyed.length) {
      setRings({});
      setPick("");
      ids.current = {};
      return;
    }
    const next = {};
    const nextIds = {};
    for (const z of surveyed) {
      if (z.ring && Array.isArray(z.ring) && z.ring.length >= 3) {
        next[z.label] = [...z.ring, z.ring[0]];
        nextIds[z.label] = z.id;
      }
    }
    ids.current = nextIds;
    setRings(next);
    setPick((p) => (p && next[p] ? p : Object.keys(next)[0] || ""));
  }, [surveyed, dirty]);

  const host = useRef(null);
  const map = useRef(null);

  useEffect(() => {
    const m = new maplibregl.Map({
      container: host.current,
      style: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
      center: D.CENTRE,
      zoom: 14.6,
      attributionControl: false,
    });
    map.current = m;
    m.on("load", () => {
      m.addSource("z", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      m.addLayer({
        id: "z-fill", type: "fill", source: "z",
        paint: {
          "fill-color": ["coalesce", ["get", "tone"], "#6B7A78"],
          "fill-opacity": ["case", ["get", "on"], 0.32, 0.1],
        },
      });
      m.addLayer({
        id: "z-line", type: "line", source: "z",
        paint: {
          "line-color": ["coalesce", ["get", "tone"], "#6B7A78"],
          "line-width": ["case", ["get", "on"], 3.1, 1.35],
          "line-blur": ["case", ["get", "on"], 0.65, 0.15],
          "line-opacity": ["case", ["get", "on"], 1, 0.78],
        },
      });
      m.addSource("dz", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      m.addLayer({
        id: "dz-line", type: "line", source: "dz",
        paint: { "line-color": "#FFB65C", "line-width": 2, "line-dasharray": [2, 1.6] },
      });
      m.addLayer({
        id: "dz-pt", type: "circle", source: "dz", filter: ["==", "$type", "Point"],
        paint: {
          "circle-radius": 4.5, "circle-color": "#0B1114",
          "circle-stroke-color": "#FFB65C", "circle-stroke-width": 1.8,
        },
      });
      setReadyTick((t) => t + 1);
    });
    return () => { m.remove(); map.current = null; };
  }, []);

  useEffect(() => {
    const m = map.current;
    if (!m) return;
    const marks = [];
    register.filter((d) => d.ll).forEach((d) => {
      const el = document.createElement("div");
      el.className = "zdot";
      el.style.setProperty("--kc", kindOf(d.kind).tone);
      el.title = d.label;
      marks.push(new maplibregl.Marker({ element: el }).setLngLat(d.ll).addTo(m));
    });
    return () => { marks.forEach((x) => x.remove()); };
  }, [register]);

  useEffect(() => {
    const m = map.current;
    const s = m && m.getSource("z");
    if (!s) return;
    s.setData({
      type: "FeatureCollection",
      features: Object.entries(rings).map(([n, r]) => ({
        type: "Feature",
        properties: { name: n, on: n === pick, tone: placesEntityToneOf(n, "zone") },
        geometry: { type: "Polygon", coordinates: [r] },
      })),
    });
  }, [rings, pick, readyTick]);

  /* Survey Bridge: MapLibre must track aperture size while the rail animates. */
  useEffect(() => {
    if (!embed) return undefined;
    const el = host.current;
    if (!el) return undefined;
    const kick = () => {
      const m = map.current;
      if (!m) return;
      try { m.resize(); } catch { /* */ }
    };
    kick();
    const t = setTimeout(kick, 360);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => kick());
      ro.observe(el);
      const aperture = el.closest(".places-brief__aperture");
      if (aperture) ro.observe(aperture);
    }
    return () => {
      clearTimeout(t);
      if (ro) ro.disconnect();
    };
  }, [embed, sheetOpen, readyTick]);

  /* Live frame the selected ring so config + map stay in dialogue. */
  useEffect(() => {
    if (!embed || !sheetOpen) return;
    const m = map.current;
    const ring = rings[pick];
    if (!m || !ring || ring.length < 3) return;
    try {
      const b = ring.reduce(
        (acc, p) => {
          acc[0] = Math.min(acc[0], p[0]);
          acc[1] = Math.min(acc[1], p[1]);
          acc[2] = Math.max(acc[2], p[0]);
          acc[3] = Math.max(acc[3], p[1]);
          return acc;
        },
        [ring[0][0], ring[0][1], ring[0][0], ring[0][1]],
      );
      m.fitBounds([[b[0], b[1]], [b[2], b[3]]], { padding: 56, duration: 520, maxZoom: 17 });
    } catch { /* */ }
  }, [embed, sheetOpen, pick, rings]);

  useEffect(() => {
    const m = map.current;
    const s = m && m.getSource("dz");
    if (!s) return;
    const f = pending.map((p) => ({ type: "Feature", geometry: { type: "Point", coordinates: p } }));
    if (pending.length > 1) {
      f.push({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: draw ? pending : [...pending, pending[0]],
        },
      });
    }
    s.setData({ type: "FeatureCollection", features: f });
  }, [pending, draw, readyTick]);

  useEffect(() => {
    const m = map.current;
    if (!m) return;
    m.getCanvas().style.cursor = draw ? "crosshair" : "";
    if (!draw) return;
    const click = (e) => setPending((v) => [...v, [+e.lngLat.lng.toFixed(6), +e.lngLat.lat.toFixed(6)]]);
    /* MapLibre best-practice close: double-click finishes polygon (≥3 verts). */
    const dbl = (e) => {
      try {
        e.preventDefault();
      } catch {
        /* */
      }
      setPending((v) => {
        if (v.length < 3) {
          toast && toast("Need three corners before double-click close", "warn");
          return v;
        }
        const name = "Zone " + (Object.keys(rings).length + 1);
        setRings((r) => ({ ...r, [name]: [...v, v[0]] }));
        setPick(name);
        setDraw(false);
        setDirty(true);
        if (onFloatOpen) onFloatOpen(true);
        toast && toast("Ring closed — save when ready", "warn");
        return [];
      });
    };
    m.on("click", click);
    m.on("dblclick", dbl);
    return () => {
      m.off("click", click);
      m.off("dblclick", dbl);
      m.getCanvas().style.cursor = "";
    };
  }, [draw, readyTick, rings, toast]);

  const reRead = () => {
    posture.reload && posture.reload();
    zonesApi.reload && zonesApi.reload();
    devicesApi.reload && devicesApi.reload();
    bumpLiveZones({ reason: "places-reread", site });
    toast && toast("Zones refreshed", "go");
  };

  const openFloat = () => { if (onFloatOpen) onFloatOpen(true); };

  const commit = () => {
    if (!writeOk) {
      toast && toast("Geometry write closed for this seat", "warn");
      return;
    }
    if (pending.length < 3) {
      toast && toast("A zone needs at least three corners", "bad");
      return;
    }
    const name = "Zone " + (Object.keys(rings).length + 1);
    setRings((r) => ({ ...r, [name]: [...pending, pending[0]] }));
    setPick(name);
    setPending([]);
    setDraw(false);
    setDirty(true);
    openFloat();
    toast && toast("Ring closed — save when ready", "warn");
  };

  /** Creative Live seed: pad bbox around all mapped pins for this seat · still needs Save. */
  const inventFromPlant = () => {
    if (!writeOk) {
      toast && toast("Geometry write closed for this seat", "warn");
      return;
    }
    const ring = ringAroundPlantOf(register);
    if (!ring) {
      toast && toast("No mapped plant pins — place devices on the map first", "warn");
      return;
    }
    let name = "Plant yard";
    let n = 2;
    while (rings[name]) {
      name = "Plant yard " + n;
      n += 1;
    }
    setRings((r) => ({ ...r, [name]: ring }));
    setPick(name);
    setPending([]);
    setDraw(false);
    setDirty(true);
    openFloat();
    toast && toast("Plant yard draft — save to keep it", "go");
    try {
      const m = map.current;
      if (m && ring.length) {
        const b = ring.reduce(
          (acc, p) => {
            acc[0] = Math.min(acc[0], p[0]);
            acc[1] = Math.min(acc[1], p[1]);
            acc[2] = Math.max(acc[2], p[0]);
            acc[3] = Math.max(acc[3], p[1]);
            return acc;
          },
          [ring[0][0], ring[0][1], ring[0][0], ring[0][1]],
        );
        m.fitBounds(
          [
            [b[0], b[1]],
            [b[2], b[3]],
          ],
          { padding: 48, duration: 700 },
        );
      }
    } catch {
      /* */
    }
  };

  const memberIds = new Set((surveyed.find((z) => z.label === pick) || {}).deviceIds || []);
  const mismatch = (surveyed.find((z) => z.label === pick) || {}).mismatch || [];
  const countIn = (n) => ((surveyed.find((z) => z.label === n) || {}).count) || 0;
  const zone = rings[pick];
  const inside = register.filter((d) => memberIds.has(d.id));
  const selectedSpaceId = ids.current[pick] || "";
  const policyApi = useApi(
    selectedSpaceId
      ? `/resource-fabric/zones/${encodeURIComponent(selectedSpaceId)}/dispatch-policy?site_id=${encodeURIComponent(site)}`
      : "",
  );
  const [policyManager, setPolicyManager] = useState("");
  const [policyGroup, setPolicyGroup] = useState("");
  const [policyMode, setPolicyMode] = useState("inside_first");
  const [policyEnabled, setPolicyEnabled] = useState(false);
  const [policyDirty, setPolicyDirty] = useState(false);
  /* Preferred crew = Roster tags — never Machine Group ink names (ontology freeze). */
  const crewOptions = useMemo(
    () => placesPreferredCrewOptionsOf(personnel, policyGroup),
    [personnel, policyGroup],
  );
  const managerOptions = useMemo(
    () => zoneDispatchManagerOptionsOf(personnel, policyManager),
    [personnel, policyManager],
  );
  const priorityHint = useMemo(() => {
    const hit = ZONE_DISPATCH_PRIORITY_OPTIONS.find((o) => o.value === policyMode);
    return hit ? hit.hint : "";
  }, [policyMode]);

  useEffect(() => {
    if (policyDirty || !policyApi.data) return;
    const policy = policyApi.data.policy || {};
    setPolicyManager(String(policy.manager_username || ""));
    setPolicyGroup(String(policy.preferred_crew || policy.preferred_group || ""));
    setPolicyMode(String(policy.ai_priority_mode || "inside_first"));
    setPolicyEnabled(policy.enabled === true);
  }, [policyApi.data, policyDirty, selectedSpaceId]);

  useEffect(() => {
    setPolicyDirty(false);
  }, [selectedSpaceId]);

  const save = async () => {
    if (!writeOk) {
      toast && toast("Geometry write closed for this seat", "warn");
      return;
    }
    const ring = rings[pick];
    if (!ring) return;
    let spaceId = ids.current[pick];
    const created = !spaceId;
    if (!spaceId) {
      spaceId = newZoneSpaceId();
      ids.current[pick] = spaceId;
    }
    const ok = await save_.run(
      path("put__resource-fabric_spaces_p", { pathParams: [spaceId] }),
      {
        method: "PUT",
        body: {
          label: pick,
          kind: "zone",
          site_id: site,
          wgs84_polygon: ring.slice(0, -1),
        },
      },
    );
    if (ok) {
      setDirty(false);
      toast && toast((created ? "Zone created · " : "Zone saved · ") + pick, "go");
      bumpLiveZones({ reason: created ? "zone-create" : "zone-save", site, label: pick, spaceId });
      reRead();
    } else {
      if (created) delete ids.current[pick];
      toast && toast(softFleetZonesErrorOf(save_.error || "Could not save zone"), "bad");
    }
  };

  const removeZone = async () => {
    if (!pick) return;
    if (!writeOk) {
      toast && toast("Geometry write closed for this seat", "warn");
      return;
    }
    if (inside.length > 0) {
      toast && toast("Move devices out before deleting this zone", "warn");
      return;
    }
    const spaceId = ids.current[pick];
    if (spaceId) {
      const ok = await del_.run(
        path("delete__resource-fabric_spaces_p", {
          pathParams: [spaceId],
          query: { site_id: site },
        }),
        { method: "DELETE" },
      );
      if (!ok) {
        toast && toast(softFleetZonesErrorOf(del_.error || "Could not delete zone"), "bad");
        return;
      }
      delete ids.current[pick];
    }
    const label = pick;
    const { [pick]: _drop, ...rest } = rings;
    setRings(rest);
    setPick(Object.keys(rest)[0] || "");
    setDirty(false);
    toast && toast(spaceId ? (label + " deleted") : (label + " draft discarded"), "warn");
    if (spaceId) {
      bumpLiveZones({ reason: "zone-delete", site, label, spaceId });
      reRead();
    }
  };

  const savePolicy = async () => {
    if (!writeOk) {
      toast && toast("Geometry write closed for this seat", "warn");
      return;
    }
    if (!selectedSpaceId) {
      toast && toast("Save the Zone boundary before adding dispatch policy", "warn");
      return;
    }
    const out = await policySave.run(
      `/resource-fabric/zones/${encodeURIComponent(selectedSpaceId)}/dispatch-policy?site_id=${encodeURIComponent(site)}`,
      {
        method: "PUT",
        body: {
          manager_username: policyManager.trim(),
          preferred_crew: policyGroup.trim(),
          preferred_group: policyGroup.trim(),
          ai_priority_mode: policyMode,
          enabled: policyEnabled,
        },
      },
    );
    if (!out) {
      toast && toast(softFleetZonesErrorOf(policySave.error || "Could not save Zone dispatch policy"), "bad");
      return;
    }
    setPolicyDirty(false);
    toast && toast("Zone dispatch policy saved", "go");
    policyApi.reload && policyApi.reload();
    bumpLiveZones({ reason: "zone-policy", site, spaceId: selectedSpaceId });
  };

  const jumpFind = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    const q = String(findQ || "").trim();
    if (!q) {
      toast && toast("Enter a place name or lat,lng", "warn");
      return;
    }
    const m = map.current;
    if (!m) return;
    setFindBusy(true);
    try {
      const zoneHit = Object.keys(rings).find((n) => n.toLowerCase().includes(q.toLowerCase()));
      if (zoneHit && rings[zoneHit] && rings[zoneHit].length >= 3) {
        setPick(zoneHit);
        if (onFloatOpen) onFloatOpen(true);
        toast && toast(`Zone · ${zoneHit}`, "go");
        return;
      }
      const pair = parseLatLngPair(q);
      let ll = null;
      let zoom = 14;
      let label = q;
      let kind = "place";
      if (pair) {
        ll = [pair.lng, pair.lat];
        label = pair.label;
        kind = "coord";
        zoom = 15.2;
      } else {
        const node = register.find((d) => {
          const blob = `${d.label || ""} ${d.id || ""}`.toLowerCase();
          return blob.includes(q.toLowerCase());
        });
        if (node && node.ll) {
          ll = node.ll;
          label = node.label || node.id;
          kind = "node";
          zoom = 15.6;
        } else {
          const hit = await geocodeOpenMeteo(q);
          if (!hit) {
            toast && toast("No place matched that search", "warn");
            return;
          }
          ll = [hit.lng, hit.lat];
          label = hit.label || q;
          zoom = 11.2;
        }
      }
      m.flyTo({ center: ll, zoom, duration: 780, essential: true });
      toast && toast(`${kind === "coord" ? "Point" : kind === "node" ? "Node" : "Place"} · ${label}`, "go");
    } catch {
      toast && toast("Place search is not available right now", "warn");
    } finally {
      setFindBusy(false);
    }
  };

  if (!posture.loading && post.enabled !== true) {
    return html`<div className="fleet-zones-desk fleet-zones-desk--liquid"
      data-cmc-page="page142-zones" data-cmc-legacy="page71-zones"
      data-cmc-residual="page173-zones">
      <div className="fleet-zones-desk__rail">
        <div>
          <p className="eyebrow">Fleet · Zones</p>
          <h2 className="fleet-zones-desk__title">Seat closed</h2>
          <p className="note">Your seat cannot open Boundaries on this grant.</p>
        </div>
        <div className="btn-row fleet-zones-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" disabled=${posture.loading}
            onClick=${reRead}><${Ico} d="refresh" w=${14}/> Refresh</button>
        </div>
      </div>
      <${Card} title="Your seat" meta=${post.posture_label || "Seat closed"}>
        <p className="note">${post.posture_line || detail || "Seat closed"}</p>
      <//>
    </div>`;
  }

  const mapBlock = html`<div className="zwrap" data-embedded=${embed ? "1" : "0"}>
      <div className="zmap" ref=${host}/>
      ${embed ? html`<form className="places-brief__find" role="search" aria-label="Find place or coordinates"
        onSubmit=${jumpFind}>
        <input type="search" value=${findQ} placeholder="Place · zone · lat,lng"
          aria-label="Search place, zone, or latitude longitude"
          onInput=${(e) => setFindQ(e.target.value)}
          disabled=${findBusy}/>
        <button type="submit" className="places-brief__act" disabled=${findBusy}>
          ${findBusy ? "…" : html`<${Ico} d="search" w=${13}/>`}
        </button>
      </form>` : null}
      <div className=${embed ? "ztools places-brief__tools" : "ztools glass"}>
        ${!writeOk ? html`
          <span className="ztools__hint">Draw tools closed · geometry write not open for this seat</span>`
        : !draw ? html`
          <${F}>
            <button type="button" className="btn btn--go btn--sm places-brief__act"
              onClick=${() => { setPending([]); setDraw(true); }}>
              <${Ico} d="plus" w=${13}/> Draw a zone</button>
            <button type="button" className="btn btn--ghost btn--sm places-brief__act"
              title="Pad a square yard around all mapped pins — still Save when ready"
              disabled=${!register.some((d) => d && d.ll)}
              onClick=${inventFromPlant}>
              Plant yard · auto</button>
            <button type="button" className="btn btn--ghost btn--sm places-brief__act" onClick=${reRead}
              disabled=${posture.loading || zonesApi.loading || devicesApi.loading}>
              <${Ico} d="refresh" w=${14}/> Refresh
            </button>
          <//>` : html`
          <${F}>
            <span className="ztools__hint">Click corners · double-click to close · ${pending.length} placed.</span>
            <button type="button" className="btn btn--go btn--sm places-brief__act" disabled=${pending.length < 3}
              onClick=${commit}>Close the ring</button>
            <button type="button" className="btn btn--text btn--sm places-brief__act"
              onClick=${() => { setPending([]); setDraw(false); }}>Cancel · UI</button>
          <//>`}
      </div>
    </div>`;

  if (embed) {
    const offMap = register.filter((d) => d && !d.ll);
    const zoneCount = Object.keys(rings).length;
    const openSheet = () => { if (onFloatOpen) onFloatOpen(true); };
    const closeSheet = () => { if (onFloatOpen) onFloatOpen(false); };
    const toggleSheet = () => { if (onFloatOpen) onFloatOpen(!sheetOpen); };
    return html`<div className="fleet-zones-desk fleet-zones-desk--embedded"
      data-cmc-places-map="1" data-survey=${surveyOk ? "1" : "0"} data-write=${writeOk ? "1" : "0"}
      data-sheet=${sheetOpen ? "1" : "0"} data-bridge="1">
      <div className="places-brief__aperture">
        ${err ? html`<${Alert} tone="warn" title="Could not load zones or devices"
          say=${softFleetZonesErrorOf(err)}/>` : null}
        ${(save_.error || del_.error) ? html`<${Alert} tone="warn" title="Could not save zone"
          say=${softFleetZonesErrorOf(save_.error || del_.error)}/>` : null}
        <div className="places-brief__seat" aria-label="Seat honesty">
          <i data-tone=${surveyOk ? "go" : "warn"} title=${surveyOk ? "Survey open" : "Survey closed"}>
            <b></b><em>Survey</em></i>
          <i data-tone=${writeOk ? "go" : "dim"} title=${writeOk ? "Write open" : "Write closed"}>
            <b></b><em>Write</em></i>
          <i data-tone=${zonesApi.data && zonesApi.data.polygons_armed ? "go" : "dim"}
            title=${zonesApi.data && zonesApi.data.polygons_armed ? "Polygons armed" : "Polygons off"}>
            <b></b><em>Poly</em></i>
        </div>
        ${mapBlock}
        <button type="button" className="places-brief__fab" data-on=${sheetOpen ? "1" : "0"}
          aria-expanded=${sheetOpen ? "true" : "false"}
          aria-controls="places-zone-bridge"
          onClick=${toggleSheet}>
          <${Ico} d="layers" w=${14}/>
          <b>Zones</b>
          <em>${zoneCount}</em>
        </button>
      </div>
      <aside className="places-brief__bridge" id="places-zone-bridge" data-open=${sheetOpen ? "1" : "0"}
        aria-label="Zone instrument" aria-hidden=${sheetOpen ? "false" : "true"}>
        <header className="places-brief__bridge-head" data-linked=${pick ? "1" : "0"}>
          <div>
            <span>Zone instrument</span>
            <b>Zones · ring dock</b>
            <small>${pick
              ? html`Linked · <em>${pick}</em> lights on the map as you edit.`
              : "Edit here — rings light on the map at the same time."}</small>
          </div>
          <button type="button" className="btn btn--ghost btn--sm" onClick=${closeSheet}>
            <${Ico} d="close" w=${14}/> Hide
          </button>
        </header>
        <div className="places-brief__zone-split">
          <div className="places-brief__zone-list">
            <h3>Zones on this work</h3>
            ${Object.entries(rings).map(([n, r]) => html`
              <button key=${n} type="button" className="zrow places-brief__zrow" data-on=${n === pick ? "1" : "0"}
                style=${{ "--ztone": placesEntityToneOf(n, "zone") }}
                onClick=${() => { setPick(n); openSheet(); }}>
                <i className="places-brief__swatch" aria-hidden="true"></i>
                <span className="zrow__t"><b>${n}</b>
                  <em>${r.length - 1} corners · ${Math.round(areaM2(r)).toLocaleString()} m²</em></span>
                <span className="zrow__n">${countIn(n)}</span>
              </button>`)}
            ${!zoneCount ? html`<p className="note places-brief__empty">
              ${writeOk ? "Draw on the map — the ring appears live, then save here." : "No rings yet · survey only on this seat."}
            </p>` : null}
            ${offMap.length ? html`<div className="places-brief__offmap" data-count=${offMap.length}>
              <h4>Not on map</h4>
              <p>These nodes have no pin — they cannot join a Zone ring until mapped.</p>
              <ul>
                ${offMap.slice(0, 8).map((d) => html`<li key=${d.id}><b>${d.label}</b>
                  <em>${d.group || "no group"}</em></li>`)}
              </ul>
              ${offMap.length > 8 ? html`<small>+${offMap.length - 8} more</small>` : null}
            </div>` : null}
          </div>
          <div className="places-brief__zone-dock" data-has=${zone ? "1" : "0"}
            style=${zone ? { "--ztone": placesEntityToneOf(pick, "zone") } : undefined}>
            ${!zone ? html`<div className="places-brief__dock-idle">
              <span>Ring dock</span>
              <b>${writeOk ? "Select or draw a ring" : "Survey only"}</b>
              <p>Rank candidates only change who is tried first — not consent or lease.</p>
            </div>` : html`
              <header className="places-brief__dock-head" style=${{ "--ztone": placesEntityToneOf(pick, "zone") }}>
                <i className="places-brief__swatch" aria-hidden="true"></i>
                <div>
                  <b>${pick}</b>
                  <small>${ids.current[pick] ? "saved ring" : "draft ring"}</small>
                </div>
              </header>
              <div className="places-brief__facts" aria-label="Ring facts">
                <div><span>Corners</span><b>${zone.length - 1}</b></div>
                <div><span>Area</span><b>${Math.round(areaM2(zone)).toLocaleString()} m²</b></div>
                <div><span>Nodes</span><b>${inside.length || "—"}</b></div>
              </div>
              ${inside.length && inside.some((d) => d.zone && d.zone !== pick) ? html`
                <${Alert} tone="warn" title="Caption mismatch"
                  say="Some nodes inside this ring still carry a different zone title. Title is not membership."/>` : null}
              <section className="zone-dispatch-policy zone-dispatch-policy--stack"
                data-zone-policy=${selectedSpaceId ? "saved" : "draft"}>
                <div className="zone-dispatch-policy__head">
                  <div>
                    <span>Rank candidates</span>
                    <small>Who is tried first — not consent, lease, or approval.</small>
                  </div>
                  <label className="zone-dispatch-policy__enabled">
                    <input type="checkbox" checked=${policyEnabled}
                      disabled=${!writeOk}
                      onChange=${(event) => { setPolicyEnabled(event.target.checked); setPolicyDirty(true); }}/>
                    <span>Use policy</span>
                  </label>
                </div>
                <div className="zone-dispatch-policy__stack">
                  <label>
                    <span>Zone manager</span>
                    <${CraftSelect} aria-label="Zone manager" value=${policyManager} allowEmpty
                      disabled=${!writeOk}
                      placeholder="No manager selected"
                      options=${managerOptions.map((opt) => ({ value: opt.value, label: opt.label, hint: opt.value || "" }))}
                      onChange=${(event) => { setPolicyManager(event.target.value); setPolicyDirty(true); }}/>
                  </label>
                  <label>
                    <span>Preferred crew</span>
                    <${CraftSelect} aria-label="Preferred crew" value=${policyGroup} allowEmpty
                      disabled=${!writeOk}
                      placeholder="No preferred crew"
                      options=${crewOptions}
                      onChange=${(event) => { setPolicyGroup(event.target.value); setPolicyDirty(true); }}/>
                    <em className="zone-dispatch-policy__hint">Roster crew tag — not a Machine Group ink.</em>
                  </label>
                  <label>
                    <span>Rank rule</span>
                    <${CraftSelect} aria-label="Rank rule" value=${policyMode}
                      disabled=${!writeOk}
                      placeholder="Choose rank rule…"
                      options=${ZONE_DISPATCH_PRIORITY_OPTIONS.map((opt) => ({
                        value: opt.value, label: opt.label, hint: opt.hint,
                      }))}
                      onChange=${(event) => { setPolicyMode(event.target.value); setPolicyDirty(true); }}/>
                    <em className="zone-dispatch-policy__hint">${priorityHint}</em>
                  </label>
                </div>
                <div className="zone-dispatch-policy__foot">
                  <button type="button" className="btn btn--go btn--sm places-brief__act"
                    data-cmc-zone-policy-ok=${writeOk ? "1" : "0"}
                    disabled=${!writeOk || !selectedSpaceId || !policyDirty || policySave.busy}
                    onClick=${savePolicy}>${policySave.busy ? "Saving…" : "Save rank"}</button>
                  <button type="button" className="btn btn--ghost btn--sm places-brief__act"
                    disabled=${!writeOk || save_.busy} onClick=${save}>Save zone</button>
                  <button type="button" className="btn btn--ghost btn--sm places-brief__act"
                    disabled=${!writeOk || del_.busy || inside.length > 0} onClick=${removeZone}>Delete</button>
                </div>
              </section>
            `}
          </div>
        </div>
      </aside>
    </div>`;
  }

  return html`<div className="fleet-zones-desk fleet-zones-desk--liquid"
    data-cmc-page="page142-zones" data-cmc-legacy="page71-zones"
    data-cmc-residual="page173-zones">
    <div className="fleet-zones-desk__rail">
      <div>
        <p className="eyebrow">Fleet · Zones</p>
        <h2 className="fleet-zones-desk__title">Boundaries</h2>
        <p className="note">
          ${post.posture_line || "Survey Live rings; save and delete only when write is open."}
        </p>
      </div>
      <div className="btn-row fleet-zones-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
          disabled=${posture.loading || zonesApi.loading || devicesApi.loading}>
          <${Ico} d="refresh" w=${14}/> Refresh
        </button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => onOpenGroups ? onOpenGroups() : go("/places/groups")}>Groups</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/ops")}>Ops map</button>
      </div>
    </div>

    <${Alert} tone="info" title="Boundaries"
      say="Survey Live rings for this seat. Save and delete run only when geometry write is open. Draft tools stay off when write is closed."/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <${Card} title="Your seat" meta=${post.posture_label || (writeOk ? "Write open" : "Survey only")}>
      <div className="btn-row" style=${{ flexWrap: "wrap", gap: "8px 14px" }}>
        <${KV} k="survey" v=${surveyOk ? "open" : "closed"}/>
        <${KV} k="geometry write" v=${writeOk ? "open" : "closed"}/>
        <${KV} k="polygon bus"
          v=${zonesApi.data && zonesApi.data.polygons_armed != null
            ? (zonesApi.data.polygons_armed ? "armed" : "off")
            : "—"}/>
        <${KV} k="membership"
          v=${(zonesApi.data && zonesApi.data.membership) || "geometry"}/>
        <${KV} k="draft mode" v=${"local only"}/>
      </div>
      ${detail ? html`<p className="note" style=${{ marginTop: 8 }}>${post.posture_line || detail}</p>` : null}
      ${!writeOk && surveyOk ? html`
        <p className="note" style=${{ marginTop: 8 }}>
          Geometry write needs a tenant admin. This seat can survey rings only.
        </p>` : null}
    <//>

    ${err ? html`
      <${Alert} tone="warn" title="Could not load zones or devices"
        say=${softFleetZonesErrorOf(err)}/>`
    : null}
    ${(save_.error || del_.error) ? html`
      <${Alert} tone="warn" title="Could not save zone"
        say=${softFleetZonesErrorOf(save_.error || del_.error)}/>` : null}

    <div className="fleet-zones-desk__kpis" role="group" aria-label="Zones KPIs">
      ${[
        ["Zones", kpis.zones, kpis.zones ? "1" : "0", ""],
        ["Devices", kpis.devices, kpis.devices ? "1" : "0", ""],
        ["Mapped pins", kpis.pinned, kpis.pinned ? "1" : "0", ""],
        ["Inside rings", kpis.housed, kpis.housed ? "1" : "0", ""],
      ].map(([k, v, on, tone]) => html`
        <div key=${k} className="fleet-zones-desk__kpi" data-on=${on} data-tone=${tone}>
          <span>${k}</span>
          <b><${Ticker} value=${v}/></b>
          <em>Live survey</em>
        </div>`)}
    </div>

    <${Card} title="Zones" meta="the ground a rule is allowed to talk about"
        sub="Closed rings of surveyed coordinates only — no simplified stand-in geometry.">
      ${mapBlock}
    <//>

    <div className="grid g2">
      <${Card} title="On this site" meta=${Object.keys(rings).length + " zones"}>
        <div className="zlist">
          ${Object.entries(rings).map(([n, r]) => html`
            <button key=${n} type="button" className="zrow" data-on=${n === pick ? "1" : "0"}
              onClick=${() => setPick(n)}>
              <span className="zrow__t"><b>${n}</b>
                <em>${r.length - 1} corners · ${Math.round(areaM2(r)).toLocaleString()} m²</em></span>
              <span className="zrow__n">${countIn(n)}</span>
            </button>`)}
          ${!Object.keys(rings).length ? html`<p className="note">No zones yet — draw one when write is open.</p>` : null}
        </div>
        <p className="note">The count is geometric membership from Live survey, not a free-text zone string on the device.</p>
      <//>

      ${!zone ? html`
        <${Card} title="No boundary selected" meta="nothing surveyed yet">
          <p className="sub">${writeOk
            ? "No surveyed boundary yet. Draw one, close the ring, then save."
            : "No surveyed boundary on this seat. Draw tools stay off while write is closed."}</p>
        <//>` : html`
        <${Card} title=${pick}
            meta=${ids.current[pick] ? "saved on server" : "draft · not saved yet"}>
          <${KV} k="Corners" v=${(zone.length - 1) + " points"}/>
          <${KV} k="Area" v=${Math.round(areaM2(zone)).toLocaleString() + " m²"}/>
          <${KV} k="Centre" v=${centroid(zone).map((x) => x.toFixed(5)).join(", ")}/>
          <${KV} k="Devices" v=${inside.length ? inside.map((d) => d.label).join(" · ") : "none"}/>
          <${Sec} title="Corners">
            <div className="corners">
              ${zone.slice(0, -1).map((p, i) => html`
                <div key=${i} className="corner"><i>${i + 1}</i>
                  <code>${p[0].toFixed(5)}, ${p[1].toFixed(5)}</code></div>`)}
            </div>
          <//>
          <section className="zone-dispatch-policy" data-zone-policy=${selectedSpaceId ? "saved" : "draft"}>
            <div className="zone-dispatch-policy__head">
              <div><span>Rank candidates</span>
                <small>This only changes candidate ranking — not consent, lease, or approval. Preferred crew is not a Machine Group.</small></div>
              <label className="zone-dispatch-policy__enabled">
                <input type="checkbox" checked=${policyEnabled}
                  disabled=${!writeOk}
                  onChange=${(event) => { setPolicyEnabled(event.target.checked); setPolicyDirty(true); }}/>
                <span>Use this policy</span>
              </label>
            </div>
            ${policyApi.error ? html`<${Alert} tone="warn" title="Dispatch policy unavailable"
              say="The Zone geometry is still valid. Candidate ranking stays nearest-first until this policy service returns."/>` : null}
            <div className="zone-dispatch-policy__grid">
              <label>
                <span>Zone manager</span>
                <${CraftSelect}
                  aria-label="Zone manager"
                  value=${policyManager}
                  disabled=${!writeOk}
                  allowEmpty
                  placeholder="No manager selected"
                  options=${managerOptions.map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                    hint: opt.value || "",
                  }))}
                  onChange=${(event) => { setPolicyManager(event.target.value); setPolicyDirty(true); }}/>
                <em className="zone-dispatch-policy__hint">
                  ${peopleApi.loading
                    ? "Loading people…"
                    : (managerOptions.length
                      ? "Choose the person AI should prefer when “Manager first” is on."
                      : "No people on roster yet — open Response → People first.")}
                </em>
              </label>
              <label>
                <span>Preferred crew</span>
                <${CraftSelect}
                  aria-label="Preferred crew"
                  value=${policyGroup}
                  disabled=${!writeOk}
                  allowEmpty
                  placeholder="No preferred crew"
                  options=${crewOptions}
                  onChange=${(event) => { setPolicyGroup(event.target.value); setPolicyDirty(true); }}/>
                <em className="zone-dispatch-policy__hint">
                  Roster crew tag for ranking people — not a Machine Group ink.
                </em>
              </label>
              <label>
                <span>AI priority rule</span>
                <${CraftSelect}
                  aria-label="AI priority rule"
                  value=${policyMode}
                  disabled=${!writeOk}
                  placeholder="Choose priority rule…"
                  options=${ZONE_DISPATCH_PRIORITY_OPTIONS.map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                    hint: opt.hint,
                  }))}
                  onChange=${(event) => { setPolicyMode(event.target.value); setPolicyDirty(true); }}/>
                <em className="zone-dispatch-policy__hint">${priorityHint}</em>
              </label>
              <div className="zone-dispatch-policy__save">
                <button type="button" className="btn btn--go btn--sm"
                  data-cmc-zone-policy-ok=${writeOk ? "1" : "0"}
                  disabled=${!writeOk || !selectedSpaceId || !policyDirty || policySave.busy}
                  onClick=${savePolicy}>${policySave.busy ? "Saving…" : "Save dispatch policy"}</button>
                ${!selectedSpaceId ? html`<em className="zone-dispatch-policy__hint">Save the Zone boundary first.</em>` : null}
              </div>
            </div>
            <p className="note">This only changes candidate ranking. Consent, leases, availability, and approval gates still decide who can actually go.</p>
          </section>
          ${mismatch.length > 0 && html`
            <p className="note" style=${{ color: "#FFB65C" }}>${mismatch.length} device${mismatch.length === 1 ? "" : "s"}
              inside this ring carry a different zone name on their profile: ${mismatch.join(", ")}. The ring
              is what the rules use; the name is only a caption.</p>`}
          <div className="btn-row fleet-zones-desk__acts">
            <button type="button" className="btn btn--go btn--sm"
              disabled=${!writeOk || !dirty || save_.busy} onClick=${save}>
              ${save_.busy ? "Saving…" : (ids.current[pick] ? "Save this zone" : "Create zone")}</button>
            <button type="button" className="btn btn--danger btn--sm"
              disabled=${!writeOk || inside.length > 0 || del_.busy}
              onClick=${removeZone}>
              ${del_.busy ? "Deleting…" : "Delete zone"}</button>
            ${inside.length > 0 && html`
              <span className="note" style=${{ margin: 0 }}>${inside.length} device${inside.length === 1 ? "" : "s"} stand inside it.</span>`}
          </div>
        <//>`}
    </div>
  </div>`;
}
